import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { Subscription } from 'rxjs';
import type { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { CurrencyType, NumericType, TimeType } from 'src/AngularJS/Models/metric';
import { AgContextMenuService } from 'src/app/reusables/services/ag-context-menu.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ErrorCode } from "src/shared/models/error-code.model";
import type { ModuleManager } from 'src/shared/models/module-manager';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { ModuleManagerModuleSettings } from 'src/shared/models/module-manager/module-manager-settings.models';
import { ModuleHelperService } from '../../../reusables/services/module-helper.service';
import { CompanyGroupTypes, GaugeData, GaugeDataItem, GaugeObject, GaugeOptions, ModuleReportType, ReportGroupType } from './gauge.models';
import { GaugeService } from './gauge.service';

@Component({
    selector: 'gauge',
    templateUrl: './gauge.component.html',
    styleUrls: ['./gauge.component.scss']
})
export class GaugeComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild('gaugeContainer', { read: ElementRef }) gaugeContainer: ElementRef<HTMLDivElement>;
    @ViewChild('minMaxModal', {static: false}) minMaxModal: TemplateRef<void>;
    @Input() minMaxForm: FormGroup;

    data: GaugeDataItem;
    gaugeObject: GaugeObject;
    isNoMetric = false;
    isDateRangeTooWideIntraday = false;
    companyGroupTypes: CompanyGroupTypes[] = [];

    isAborting = false;
    isFirstLoad = true;
    isLoading = false;
    loadingPromise: Promise<ApiResponse<GaugeData>> = null;
    abortLoad: () => void = null;
    range: number;
    metricName: string;
    gaugeModalOptions: GaugeOptions;
    min: string | number = null;
    max: string | number = null;
    defaultMin: string | number;
    defaultMax: string | number;
    objectiveData = new Map<number, [string, string]>();
    selectedDateText: string;
    hasObjectives: boolean;
    errorMessage: string;
    minMaxState = null;
    validMin:  number;
    validMax: number;
    precision = 2; // for edge case with time objectives in minutes/hours
    formSubscriptions: Subscription[] = [];

    private stateSaveTimeout: NodeJS.Timeout; // For debouncing calls to queueStateSave().

    constructor(
        private formBuilder: FormBuilder,
        private moduleHelper: ModuleHelperService,
        private service: GaugeService,
        private cvLocalizationService: CVLocalizationService,
        private ngbModal: NgbModal,
        private contextMenuService?: AgContextMenuService,
    ) {
    }

    numericType(): NumericType {
        return this.data?.metric?.NumericType;
    }

    timeType(): TimeType {
        return this.data?.metric?.TimeType;
    }

    currencyType(): CurrencyType {
        return this.data?.metric?.CurrencyType;
    }

    isTimeFormat(): boolean{
        return this.numericType() === NumericType.Time && this.timeType() === TimeType.Time;
    }

    minMaxChanged(): boolean {
        const minValue = this.min?.toString() ?? null;
        const maxValue = this.max?.toString() ?? null;
        return this.minMaxForm.get('minValue').value !== minValue ||
            this.minMaxForm.get('maxValue').value !== maxValue;
    }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.moduleManager.showLoader = true;
        const localizationPromises = [
            this.cvLocalizationService.GetSection("Gauge_Module"),
            this.cvLocalizationService.GetSection("Common"),
            this.cvLocalizationService.GetSection("Gauge_Min_Max"),
        ];
        await Promise.all(localizationPromises);
        this.moduleManager.registerEvents({onDashboardActivated: () => {
            if (this.data)
                this.createGauge();
        }});

        this.moduleManager.addState({
            name: 'gaugeMinMax',
            autoLoad: true,
            getStateData: () => this.minMaxState,
            onStateLoaded: (stateData) => {
                if (stateData == null)
                    return;

                this.minMaxState = stateData;
            }
        });

        this.initForm();
        this.subscribeToValueChanges();
    }

    initForm() {
        this.minMaxForm = this.formBuilder.group({
            minValue: [
                {value: this.min, disabled: false},
                Validators.compose([
                    this.customValidatorForType.bind(this),
                    this.customValidatorForMinValue.bind(this),
                ]),
            ],
            maxValue: [
                {value: this.max, disabled: false},
                Validators.compose([
                    this.customValidatorForType.bind(this),
                    this.customValidatorForMaxValue.bind(this),
                ]),
            ]
        });
    }

    subscribeToValueChanges() {
        this.formSubscriptions.push(
            this.minMaxForm.get('minValue').valueChanges.subscribe(value => {
                const cleanedValue = this.cleanValue(value);
                if (value !== cleanedValue) {
                    this.minMaxForm.get('minValue').setValue(cleanedValue, { emitEvent: false });
                }
            })
        );

        this.formSubscriptions.push(
            this.minMaxForm.get('maxValue').valueChanges.subscribe(value => {
                const cleanedValue = this.cleanValue(value);
                if (value !== cleanedValue) {
                    this.minMaxForm.get('maxValue').setValue(cleanedValue, { emitEvent: false });
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.formSubscriptions.forEach(sub => sub.unsubscribe());
        this.formSubscriptions = [];
    }

    ngAfterViewInit(): void {
        this.moduleManager.setOptions({
            canRefresh: () => !this.isLoading,
            enableWidthClass: false,
            hasRefresh: true,
            onExport: format => this.service.loadDataForExport(this.moduleKey, format),
            onRefresh: isAutoRefresh => this.onRefresh(isAutoRefresh),
            onSettingsApplied: settings => this.onSettingsApplied(settings),
            refreshType: ModuleManagerRefreshType.Auto,
            widgetMode: false,
        });
    }

    onRefresh(isAutoRefresh: boolean) {
        this.moduleManager.showLoader = !isAutoRefresh;
        return this.loadData(isAutoRefresh);
    }

    onSettingsApplied(settings: ModuleManagerModuleSettings): void {
        // settings were reapplied, destroy gauge and recreate
        if (this.gaugeObject != null) {
            this.gaugeObject.destroy();
            this.gaugeObject = null;
        }

        this.moduleManager.showLoader = true;
        this.loadData(false);
    }

    createGauge(min?: string, max?: string): void {
        // The gauge cannot handle decimal breakpoints, so we are allowing up to thousandths place and then turning into an integer
        let max_decimal_places_index = 0;
        const gaugeData = cloneDeep(this.data?.metricData.objectiveData);
        const timeTypeScaler = this.timeType() === TimeType.Minute ? 60
            : this.timeType() === TimeType.Hour ? 3600
                : 1;
        if (gaugeData !== null) {
            gaugeData.poDetails.forEach((pdo, index) => {
                if (timeTypeScaler > 1)
                    pdo.Value = (pdo.Value / timeTypeScaler).toPrecision(4);
                const current_index = pdo.Value.toString().split(".");
                if (current_index.length > 1) {
                    const previous_index = gaugeData.poDetails[max_decimal_places_index].Value.toString().split(".");
                    if (previous_index.length > 1) {
                        if (previous_index[1].length < current_index[1].length) {
                            max_decimal_places_index = index;
                        }
                    } else {
                        max_decimal_places_index = index;
                    }

                }
            });
        }

        const objectiveValue = gaugeData?.poDetails?.[max_decimal_places_index]?.Value;
        const objectiveValueString = objectiveValue?.toString().split(".");
        let decimal_length = 0;
        if (typeof objectiveValueString !== 'undefined' && objectiveValueString.length > 1) {
            decimal_length = objectiveValueString[1]?.length;
        } else {
            decimal_length = 3;
        }

        this.setPrecision(timeTypeScaler, parseFloat(objectiveValue));

        const VALUE_MODIFIER_POWER = ++decimal_length;
        const VALUE_MODIFIER = Math.pow(10, VALUE_MODIFIER_POWER);
        const metric = this.data.metric;
        const metricData = this.data.metricData;

        // set gauge options
        const gaugeOptions: GaugeOptions = {
            // animDurationHover: 100,
            breakpoints: [],
            colorSegments: [],
            direction: metric.DefaultDirection,
            hover: true,
            // hoverInvert: true, // Set to true if the hover effect should be set to default
            minValue: 0,
            maxValue: 0,
            title: this.getMetricDisplayName(),
            unit: '',
            value: metricData.MetricValue * VALUE_MODIFIER,
            metricDisplayValue: metricData.MetricDisplayValue,
            valueFormat: (val: number) => {
                val = val / VALUE_MODIFIER;
                if (val >= gaugeOptions.maxValue / VALUE_MODIFIER) {
                    return this.moduleHelper.formatMetricValue(
                        metricData.MetricValue,
                        metric.NumericType,
                        metric.TimeType,
                        metric.CurrencyType,
                        this.precision
                    );
                } else {
                    return this.moduleHelper.formatMetricValue(val, metric.NumericType,
                        metric.TimeType, metric.CurrencyType, this.precision);
                }
            }
        };

        if (gaugeData) {
            // transform objective details array into an array of the breakpoint values
            const breakpoints = gaugeData.poDetails.map(pod => pod.Value);
            breakpoints.sort((a, b) => a - b);
            gaugeOptions.breakpoints = breakpoints.map(breakpoint => breakpoint * VALUE_MODIFIER - 1);

            const formattedGaugeBreakpoints = [this.moduleHelper.formatMetricValue(0,
                metric.NumericType, metric.TimeType, metric.CurrencyType)];

            gaugeOptions.breakpoints.forEach(breakpoint => {
                const formattedBreakpoint = this.moduleHelper.formatMetricValue((breakpoint / VALUE_MODIFIER),
                    metric.NumericType, metric.TimeType, metric.CurrencyType, this.precision);
                formattedGaugeBreakpoints.push(formattedBreakpoint);
            });

            const colors = gaugeData.poColors.map(poc => poc.ColorLabel);
            // set direction of colors to match metric direction
            if (gaugeOptions.direction === 0)
                colors.reverse();

            this.objectiveData.clear();
            formattedGaugeBreakpoints.forEach((breakpoint, index) => {
                const color = colors[index];
                this.objectiveData.set(index, [color, breakpoint]);
            });

            // create length to the last breakpoint by adding the length of the first breakpoint
            const lastValue = gaugeOptions.breakpoints[gaugeOptions.breakpoints.length - 1];
            const average = lastValue / gaugeOptions.breakpoints.length;

            const gaugeMax = this.data.metric.NumericType === NumericType.Percent && lastValue < VALUE_MODIFIER ?
                VALUE_MODIFIER : lastValue + average;
            this.setValidMinMax(formattedGaugeBreakpoints);

            if (min){
                // replace previously saved Min value is now greater than valid min value
                const convertedValue = this.isTimeFormat()  ? moment.duration(min).asSeconds() : parseFloat(min);
                const minValue = Math.min(convertedValue, this.validMin);
                this.min = this.isTimeFormat() ? this.formatTimeValues(minValue.toString()) : minValue;
                gaugeOptions.minValue = this.numericType() === NumericType.Percent ? minValue / 100 * VALUE_MODIFIER :
                    minValue * VALUE_MODIFIER;
            }

            if (max){
                const convertedValue = this.isTimeFormat() ? moment.duration(max).asSeconds() : parseFloat(max);
                const maxValue = Math.max(convertedValue, this.validMax);
                this.max = this.isTimeFormat() ? this.formatTimeValues(maxValue.toString()) : maxValue;
                const modifiedMaxValue = this.numericType() === NumericType.Percent ? maxValue / 100 * VALUE_MODIFIER  :
                    maxValue * VALUE_MODIFIER;
                const lastBreakpoint = gaugeOptions.breakpoints[gaugeOptions.breakpoints.length - 1];
                gaugeOptions.maxValue = Math.max(modifiedMaxValue, lastBreakpoint + 1);
            }
            else
                gaugeOptions.maxValue = gaugeMax;

            const defaultOrNegativeMetric = Math.min(metricData.MetricValue, 0);
            this.defaultMin = this.moduleHelper.formatMetricValue(defaultOrNegativeMetric,
                metric.NumericType, metric.TimeType, metric.CurrencyType, this.precision);
            this.defaultMax = this.moduleHelper.formatMetricValue((gaugeMax / VALUE_MODIFIER),
                metric.NumericType, metric.TimeType, metric.CurrencyType, this.precision);

            // transform objective colors array into an array of hex color segments
            gaugeOptions.colorSegments = this.data.metricData.objectiveData.poColors.map(poc => poc.HexColor);
            if (gaugeOptions.direction === 0) // reverse colors on down metrics
                gaugeOptions.colorSegments.reverse();

            switch (this.moduleManager.moduleSettings.selectedDateRange.name){
                case 'Today':
                    this.selectedDateText = this.cvLocalizationService.getCachedText('Gauge_Min_Max', 'Daily', 'Daily');
                    break;
                case 'Yesterday':
                    this.selectedDateText = this.cvLocalizationService.getCachedText('Gauge_Min_Max', 'Daily', 'Daily');
                    break;
                case 'This Week':
                    this.selectedDateText = this.cvLocalizationService.getCachedText('Gauge_Min_Max', 'Weekly', 'Weekly');
                    break;
                case 'Last 7 Days':
                    this.selectedDateText = this.cvLocalizationService.getCachedText('Gauge_Min_Max', 'Weekly', 'Weekly');
                    break;
                case 'This Month':
                    this.selectedDateText = this.cvLocalizationService.getCachedText('Gauge_Min_Max', 'Monthly', 'Monthly');
                    break;
                case 'Last 30 Days':
                    this.selectedDateText = this.cvLocalizationService.getCachedText('Gauge_Min_Max', 'Monthly', 'Monthly');
                    break;
                default:
                    this.selectedDateText = this.cvLocalizationService.getCachedText('Gauge_Min_Max',
                        'Objective_Ranges', 'Objective ranges');
                    break;

            }
        } else {
            // fake gauge
            gaugeOptions.hover = false;
            gaugeOptions.breakpoints = [];
            gaugeOptions.colorSegments = ['#a8afb9']; // gray5
            gaugeOptions.minValue = 0;
            gaugeOptions.maxValue = gaugeOptions.value + 1;
            this.resetMinMax();
            this.min = null;
            this.max = null;
        }

        if (this.gaugeObject == null) {
            this.gaugeObject = new Gauge(this.gaugeContainer.nativeElement, gaugeOptions);
        } else {
            this.gaugeObject.setOpts(gaugeOptions);
        }

        const isRenamed = metric.MetricCustomName && metric.MetricCustomName !== "";

        if(isRenamed){
        // Adding a <title> element since tspan does not have a title attr
            const titleClass = document.getElementById("titleClass");
            const titleString = this.cvLocalizationService.getCachedText("Common", "Original_Metric", "Original Metric Name")
                + ": \n" + " " + metric.Abbreviation;
            const new_html = "<title> "+ titleString+" </title>" +titleClass.innerHTML;
            document.getElementById("titleClass").innerHTML = new_html;
        }
    }

    async loadData(isAutoRefresh): Promise<void> {
        if (this.loadingPromise) { // cancel current load
            this.isAborting = true;
            this.abortLoad?.();
            this.loadingPromise = null;
            this.abortLoad = null;
        }

        this.isLoading = true;
        const abort = new Promise<void>((resolve, reject) => this.abortLoad = () => resolve());
        this.loadingPromise = this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad, abort);

        try {
            const response = await this.loadingPromise;

            if (response.status_code === 200) {
                if (response.data.Error === ErrorCode.NoMetric) {
                    this.isNoMetric = true;
                } else if (response.data.Error === ErrorCode.DateRangeTooWideIntraday) {
                    this.isDateRangeTooWideIntraday = true;
                } else {
                    this.isNoMetric = false;
                    this.isDateRangeTooWideIntraday = false;
                    this.data = response.data.metrics[0];
                    this.companyGroupTypes = response.data.companyGroupTypes;
                    if (this.isFirstLoad) {
                        this.setMenuItems();
                    }
                    this.createGauge(this.minMaxState?.min, this.minMaxState?.max);
                    this.setMenuItems();
                    this.moduleManager.setAutoRefreshReady();
                }

                this.moduleManager.showLoader = false;
            } else {
                throw response;
            }
        } catch (e) {
            if (this.isAborting) {
                this.isAborting = false;
                return;
            }

            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.loadingPromise = null;
            this.isLoading = false;
            this.isFirstLoad = false;
            this.moduleManager.container.showDateRange = true;
        }
    }

    getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Gauge_Module", key, defaultValue);
    }

    getContextMenuParams(groupType: ReportGroupType, groupLevel: number) {
        const moduleInfo = this.moduleManager?.getModuleInfo();
        const moduleSettings = this.moduleManager?.getModuleSettings();
        return {
            moduleKey: this.moduleKey,
            moduleTypeId: moduleInfo.ModuleID,
            groupData: {
                groupID: null,
                groupName: moduleSettings?.selectedGroupName,
                groupLevel,
                groupHierarchy: moduleSettings?.parentGroupLevelName,
                groupType,
                teamUUID: null,
                agentUUID: null,
            },
            isParentAgGridModule: false,
        };
    }

    setMenuItems() {
        this.moduleManager.resetMenu();

        const openReport = (groupLevel: number, moduleType: ModuleReportType, groupType: ReportGroupType) => {
            const contextMenuParams = this.getContextMenuParams(groupType, groupLevel);
            if (moduleType === ModuleReportType.Interval) {
                this.contextMenuService.intervalReportClicked.next(contextMenuParams);
            } else if (moduleType === ModuleReportType.Summary) {
                this.contextMenuService.summaryReportClicked.next(contextMenuParams);
            }
        };

        //reverse companyGroupTypes array to show the groups in the order they are in the slide-out settings
        this.companyGroupTypes = this.companyGroupTypes.reverse();

        const intervalEmpGroups = [];
        const summaryEmpGroups = [];
        const intervalContactGroups = [];
        const summaryContactGroups = [];

        const employeeGroupsHeader: { name: string; type: ModuleManagerMenuItemType }[] = [{
            name: this.getLocaleText("Employee_Groups", "Employee Groups"),
            type: ModuleManagerMenuItemType.HeaderItem,
        }];

        const contactGroupsHeader: { name: string; type: ModuleManagerMenuItemType }[] = [{
            name: this.getLocaleText("Contact_Groups", "Contact Groups"),
            type: ModuleManagerMenuItemType.HeaderItem,
        }];

        this.companyGroupTypes.forEach((groupType) => {
            if (groupType.BaseGroupType === ReportGroupType.Employee) {
                intervalEmpGroups.push({
                    name: "By "+ groupType.GroupTypeName,
                    click: () => openReport(groupType.GroupLevel, ModuleReportType.Interval, groupType.BaseGroupType),
                });
                summaryEmpGroups.push({
                    name: "By "+ groupType.GroupTypeName,
                    click: () => openReport(groupType.GroupLevel, ModuleReportType.Summary, groupType.BaseGroupType),
                });
            } else {
                intervalContactGroups.push({
                    name: "By "+ groupType.GroupTypeName,
                    click: () => openReport(groupType.GroupLevel, ModuleReportType.Interval, groupType.BaseGroupType),
                });
                summaryContactGroups.push({
                    name: "By "+ groupType.GroupTypeName,
                    click: () => openReport(groupType.GroupLevel, ModuleReportType.Summary, groupType.BaseGroupType),
                });
            }
        });

        let intervalSubItems = [];
        let summarySubItems = [];

        intervalSubItems = intervalSubItems
            .concat(employeeGroupsHeader)
            .concat(intervalEmpGroups)
            .concat([{ type: ModuleManagerMenuItemType.Divider }])
            .concat(contactGroupsHeader)
            .concat(intervalContactGroups);

        summarySubItems = summarySubItems
            .concat(employeeGroupsHeader)
            .concat(summaryEmpGroups)
            .concat([{ type: ModuleManagerMenuItemType.Divider }])
            .concat(contactGroupsHeader)
            .concat(summaryContactGroups);

        const intervalReportMenuItem: IModuleManagerMenuItem = {
            name: this.getLocaleText("Open_Interval_Rep", "Open Interval Report"),
            icon: "report",
            type: ModuleManagerMenuItemType.SubMenu,
            subItems: [
                ...intervalSubItems,
            ],
            readOnlyEnabled: true,
        };

        const summaryReportMenuItem: IModuleManagerMenuItem = {
            ...intervalReportMenuItem,
            name: this.getLocaleText("Open_Summary_Rep", "Open Summary Report"),
            subItems: [
                ...summarySubItems,
            ],
        };
        this.moduleManager.addMenuItem(intervalReportMenuItem);
        this.moduleManager.addMenuItem(summaryReportMenuItem);
        this.hasObjectives = (this.data?.metricData.objectiveData !== null && this.data?.metricData.objectiveData !== undefined);
        if (this.hasObjectives){
            this.moduleManager.addMenuItem({
                name: this.cvLocalizationService.getCachedText("Gauge_Min_Max", "Gauge_Range", "Gauge Range"),
                icon: "gaugeRange",
                type: ModuleManagerMenuItemType.MenuItem,
                getEnabled: () => this.gaugeObject != null,
                click: () => {
                    this.ngbModal.open(this.minMaxModal,
                        {centered: true, container: '#min-max-modal-container-'+this.moduleKey, modalDialogClass: 'min-max-modal'});
                    this.metricName = this.getMetricDisplayName();
                    this.range = this.gaugeObject.opts.breakpoints.length + 1;
                    this.minMaxForm.get('minValue').setValue(this.min);
                    this.minMaxForm.get('maxValue').setValue(this.max);
                },
            });
        }
    }

    resetMinMax() {
        this.minMaxForm.get('minValue').setValue(null);
        this.minMaxForm.get('maxValue').setValue(null);
    }

    saveMinMax(min: string, max: string) {
        if(!this.validateInput('minValue') || !this.validateInput('maxValue'))
            return;

        this.min = min;
        this.max = max;
        this.minMaxState = {min, max};
        this.createGauge(min, max);
        this.queueStateSave();
        this.closeModal();
    }

    validateInput(formName: string) {
        return this.minMaxForm.get(formName).valid;
    }

    setValidMinMax(objectiveBreakpoints: string[]) {
        // Min
        const metricValue = this.numericType() === NumericType.Percent ?
            this.data?.metricData.MetricValue * 100 : this.data?.metricData.MetricValue;
        const minBreakpoint = this.isTimeFormat()?
            moment.duration(objectiveBreakpoints[1]).asSeconds() : parseFloat(this.cleanValue(objectiveBreakpoints[1]));
        const minValue = Math.min(metricValue, minBreakpoint);

        const roundingPrecision = Math.pow(10, this.precision);
        const offset = 1 / roundingPrecision;

        if (this.numericType() === NumericType.Percent)
            this.validMin = Math.round((minValue - offset) * roundingPrecision) / roundingPrecision;
        else if (this.isTimeFormat())
            this.validMin = Math.floor(minValue);
        else
            this.validMin = Math.round((minValue - offset) * roundingPrecision) / roundingPrecision;
        // Max
        const breakpointLength = objectiveBreakpoints.length - 1;
        const maxBreakPoint = this.isTimeFormat()  ?
            moment.duration(objectiveBreakpoints[breakpointLength]).asSeconds() :
            parseFloat(this.cleanValue(objectiveBreakpoints[breakpointLength]));

        if (this.numericType() === NumericType.Percent)
            this.validMax = Math.round((maxBreakPoint + offset) * roundingPrecision) / roundingPrecision;
        else if (this.isTimeFormat())
            this.validMax = maxBreakPoint + 1;
        else
            this.validMax = Math.round(maxBreakPoint * roundingPrecision) / roundingPrecision;
    }

    customValidatorForMinValue(control: FormControl) {
        if(control.value){
            if(this.isTimeFormat()){
                let timeValue = control.value;
                const split = timeValue.split(':');
                if(split.length > 1)
                {
                    const join = split.filter(part => part !== '').join(':');
                    timeValue = moment.duration(join).asSeconds();
                }
                if(parseFloat(timeValue) > this.validMin)
                    return {minValueInvalid: {value: control.value}};
            }
            else{
                if(parseFloat(control.value) > this.validMin)
                    return {minValueInvalid: {value: control.value}};
            }
        }
        return null;
    }

    customValidatorForMaxValue(control: FormControl) {
        if (control.value) {
            if(this.isTimeFormat()){
                let timeValue = control.value;
                const split = timeValue.split(':');
                if(split.length > 1)
                {
                    const join = split.filter(part => part !== '').join(':');
                    timeValue = moment.duration(join).asSeconds();
                }
                if(parseFloat(timeValue) < this.validMax)
                    return {maxValueInvalid: {value: control.value}};
            }
            else{
                if(parseFloat(control.value) < this.validMax)
                    return {maxValueInvalid: {value: control.value}};
            }
        }
        return null;
    }

    customValidatorForType(control: FormControl) {
        if (control.value) {
            if(this.isTimeFormat()){
                const timeRegex = /^\d{1,4}:[0-5]?\d(:[0-5]?\d)?$/.test(control.value);
                if (control.value.toString().includes(':') && !timeRegex) {
                    return {invalidTime: {value: control.value}};
                }
            }
            else{
                const decimalRegex = /^-?(\d+|\.\d+|\d+\.\d*)$/;
                if(!decimalRegex.test(control.value))
                    return {invalidInput: {value: control.value}};
            }
        }
        return null;
    }

    formatFormTimeValues(formName: string) {
        if(this.isTimeFormat()
        && !this.minMaxForm.get(formName).hasError('invalidTime')) {
            const timeValue = this.minMaxForm.get(formName).value;
            const formattedValue = this.formatTimeValues(timeValue);
            this.minMaxForm.get(formName).setValue(formattedValue);
        }
    }

    formatTimeValues(timeValue: string): string{
        const timeParts = timeValue.split(':');
        if(timeParts.length === 1 && timeParts[0])
        {
            return this.moduleHelper.formatMetricValue(parseFloat(timeValue), this.numericType(),
                this.timeType(), this.currencyType(), this.precision);
        }
        else
        {
            const duration = moment.duration(timeValue).asSeconds();
            return this.moduleHelper.formatMetricValue(duration, this.numericType(),
                this.timeType(), this.currencyType(), this.precision);
        }
    }

    cleanValue(value: string): string {
        let cleanedValue: string = null;
        const stringValue = value?.toString();
        if (stringValue) {
            if (this.isTimeFormat()) {
                cleanedValue = stringValue.replace(/[^0-9:]+/g, ""); // Remove non-numerical chars except for :
            } else {
                const decimalSeparator = (typeof CVDecimalSymbol !== 'undefined' ? CVDecimalSymbol : '.');
                const groupSeparator = (typeof CVNumberGroupingSymbol !== 'undefined' ? CVNumberGroupingSymbol : ',');

                let temp = stringValue.replace(new RegExp('\\' + groupSeparator, 'g'), '');

                // Replace the culture's decimal separator with '.' for consistent processing
                if (decimalSeparator !== '.') {
                    temp = temp.replace(new RegExp('\\' + decimalSeparator, 'g'), '.');
                }

                cleanedValue = temp.replace(/[^0-9.-]+/g, "");

                const isNegative = cleanedValue.startsWith('-');
                cleanedValue = cleanedValue.replaceAll('-', '');

                const isDecimal = cleanedValue.indexOf('.') !== -1;
                if (isDecimal) { // If there is a decimal, keep only the first occurrence and remove all others
                    const parts = cleanedValue.split('.');
                    cleanedValue = parts[0] + '.' + parts.slice(1).join('');
                }

                if (isNegative && this.numericType() !== NumericType.Time)
                    cleanedValue = '-' + cleanedValue;
            }
        }
        return cleanedValue;
    }

    queueStateSave(){
        if (this.stateSaveTimeout) {
            clearTimeout(this.stateSaveTimeout);
        }
        this.stateSaveTimeout = setTimeout(async () => {
            this.stateSaveTimeout = null;
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    }

    displayError(control: AbstractControl): boolean {
        return control?.errors != null;
    }

    closeModal() {
        this.ngbModal.dismissAll();
    }

    getErrorMessage(control: AbstractControl): string {
        this.errorMessage = '';
        if (!control)
            return this.errorMessage;
        if (control.hasError('invalidInput'))
            this.errorMessage = this.cvLocalizationService.getCachedText('Gauge_Min_Max', 'Valid_Number', 'Please enter a valid number');
        else if (control.hasError('invalidTime'))
            this.errorMessage = this.cvLocalizationService.getCachedText('Gauge_Min_Max', 'Valid_Time', 'Please enter a valid time');
        else if (control.hasError('minValueInvalid'))
        {
            const valueToFormat = this.numericType() === NumericType.Percent ? this.validMin / 100 : this.validMin;
            const formattedValue = this.moduleHelper.formatMetricValue(valueToFormat, this.numericType(),
                this.timeType(), this.currencyType(), this.precision);
            this.errorMessage = this.cvLocalizationService.getCachedText('Gauge_Min_Max',
                'Min_Number', 'Minimum value cannot be larger than') + " " + formattedValue;
        } else if (control.hasError('maxValueInvalid'))
        {
            const valueToFormat = this.numericType() === NumericType.Percent ? this.validMax / 100 : this.validMax;
            const formattedValue = this.moduleHelper.formatMetricValue(valueToFormat, this.numericType(),
                this.timeType(), this.currencyType(), this.precision);
            this.errorMessage = this.cvLocalizationService.getCachedText('Gauge_Min_Max',
                'Max_Number', 'Maximum value cannot be smaller than') + " " + formattedValue;
        }
        return this.errorMessage;
    }

    setPrecision(timeTypeScaler: number, objectiveValue: number){
        if(timeTypeScaler > 1 && objectiveValue < 0.01){
            this.precision = objectiveValue < 0.001 ? 4 : 3;
        }
        else
            this.precision = 2;
    }

    getMetricDisplayName(): string {
        if (this.data?.metric.MetricCustomName && this.data?.metric.MetricCustomName !== "") {
            return this.data?.metric.MetricCustomName + "*";
        } else {
            return this.data?.metric.Abbreviation;
        }
    }
}
